/**
 * Control bar tooltips.
 *
 * The browser's own tooltip is not usable here. It appears after a dwell the
 * viewer does not expect, it is drawn by the OS outside the player's box — so
 * on an embed sitting near the bottom of a customer's page it is clipped by
 * the viewport or lands on top of the page's own content — and it cannot be
 * themed. This replaces it with one element inside the player.
 *
 * ONE ELEMENT, NOT ONE PER BUTTON. Buttons come and go: the chat toggle is
 * inserted when a grant arrives, the cast button when the SDK loads, and the
 * control bar is rebuilt on a tech switch. A single tooltip positioned on
 * demand has nothing to keep in sync, and the listeners are delegated so a
 * button added later is covered without being registered.
 *
 * WHERE THE LABEL COMES FROM. video.js keeps `.vjs-control-text` current as a
 * button's state changes — Play becomes Pause, Mute becomes Unmute — so the
 * label is read at hover time rather than cached. Our own buttons carry an
 * `aria-label` instead, which is read the same way, so the chat toggle's
 * unread suffix shows up in the tooltip for free.
 *
 * NATIVE TITLES ARE STRIPPED. Leaving `title` in place would show both
 * tooltips, ours immediately and the browser's a second later. video.js
 * re-adds it on every state change, so a MutationObserver moves each one to
 * `data-lp-title` as it appears rather than a one-time sweep at mount.
 *
 * TOUCH GETS NOTHING. A tooltip on a tap is a tooltip nobody asked for,
 * sitting under the finger that summoned it.
 */

const GAP = 10
const EDGE = 6

/** Where tooltips are offered. The chat header is in the list because its
 *  close button is a bare icon in a panel that can be the only thing on
 *  screen while the video buffers, and the top-right cluster because the cast
 *  buttons keep their tooltips after being lifted out of the bar. */
const SCOPES = '.vjs-control-bar, .lp-chat-header, .lp-top-controls'

/**
 * Bars, sliders and readouts are not buttons and get no tooltip, with one
 * exception: the volume panel is two controls sharing a hit area, and its
 * slider expands out from UNDER the cursor the moment the mute button is
 * hovered. Treating them separately meant the volume tooltip appeared and
 * vanished in the same frame, every time.
 */
const TARGET = 'button, .vjs-volume-panel'

export interface TooltipsHandle {
  dispose(): void
}

function labelFor(el: HTMLElement): string {
  const explicit = el.getAttribute('data-lp-tip')
  if (explicit) return explicit.trim()
  // video.js keeps this one honest across state changes.
  const controlText = el.querySelector('.vjs-control-text')?.textContent?.trim()
  if (controlText) return controlText
  const aria = el.getAttribute('aria-label')?.trim()
  if (aria) return aria
  return (el.getAttribute('data-lp-title') ?? el.getAttribute('title') ?? '').trim()
}

export function mountTooltips(stage: HTMLElement): TooltipsHandle {
  const tip = document.createElement('div')
  tip.className = 'lp-tip'
  tip.setAttribute('role', 'tooltip')
  tip.setAttribute('aria-hidden', 'true') // the button's own label already says it
  stage.appendChild(tip)

  let current: HTMLElement | null = null
  /* Last pointer position, so a pointerout can be checked against where the
     cursor actually is rather than trusted. */
  let lastX = -1
  let lastY = -1

  const hide = (): void => {
    current = null
    tip.classList.remove('lp-tip-on')
  }

  const show = (el: HTMLElement): void => {
    const label = labelFor(el)
    if (!label) {
      hide()
      return
    }
    current = el
    tip.textContent = label

    // Measure with the tooltip laid out but not yet painted, or the first
    // hover of a session positions against a zero-width box.
    tip.classList.add('lp-tip-measuring')
    const stageBox = stage.getBoundingClientRect()
    const btn = el.getBoundingClientRect()
    const tipBox = tip.getBoundingClientRect()
    tip.classList.remove('lp-tip-measuring')

    if (!stageBox.width || !btn.width) {
      hide()
      return
    }

    // Centred on the button, then pulled back inside the player. An embed can
    // be narrower than a tooltip is wide, so the clamp is written to survive
    // its own bounds crossing over.
    const wanted = btn.left - stageBox.left + btn.width / 2 - tipBox.width / 2
    const max = Math.max(EDGE, stageBox.width - tipBox.width - EDGE)
    tip.style.left = `${Math.round(Math.min(Math.max(wanted, EDGE), max))}px`

    // Above by default. The chat panel's header sits at the top of the stage,
    // where above is off the edge, so that one flips underneath.
    const above = btn.top - stageBox.top - tipBox.height - GAP
    if (above >= EDGE) {
      tip.style.top = `${Math.round(above)}px`
    } else {
      tip.style.top = `${Math.round(btn.bottom - stageBox.top + GAP)}px`
    }

    tip.classList.add('lp-tip-on')
  }

  const targetFrom = (node: EventTarget | null): HTMLElement | null => {
    if (!(node instanceof Element)) return null
    const owner = node.closest<HTMLElement>(TARGET)
    // closest() finds the mute button before the panel that wraps it, so this
    // only fires for the slider and the panel's own padding.
    const btn = owner?.classList.contains('vjs-volume-panel')
      ? owner.querySelector<HTMLElement>('.vjs-mute-control')
      : owner
    if (!btn || !btn.closest(SCOPES) || !stage.contains(btn)) return null
    if (btn.classList.contains('lp-hidden') || btn.hasAttribute('data-lp-tip-off')) return null
    return btn
  }

  const track = (ev: PointerEvent): void => {
    lastX = ev.clientX
    lastY = ev.clientY
  }

  const onOver = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') return
    track(ev)
    const btn = targetFrom(ev.target)
    if (!btn) {
      if (current) hide()
      return
    }
    if (btn !== current) show(btn)
  }

  /**
   * pointerout is not proof the pointer left. It fires when a control REFLOWS
   * under a stationary cursor — the volume panel expanding its slider is the
   * one that bites, and it took the mute tooltip down the moment it appeared.
   * So the event only schedules a check, and where the cursor actually is
   * decides.
   */
  const onOut = (ev: PointerEvent): void => {
    track(ev)
    const btn = current
    if (!btn) return
    requestAnimationFrame(() => {
      if (current !== btn) return
      const under = lastX < 0 ? null : document.elementFromPoint(lastX, lastY)
      // targetFrom, not contains: the volume slider is not inside the mute
      // button but resolves to it, and hovering it is still hovering volume.
      if (under && targetFrom(under) === btn) return
      hide()
    })
  }

  /**
   * Keyboard focus gets a tooltip; a click that happens to move focus does
   * not, or every tap-then-look leaves one hanging. :focus-visible is exactly
   * that distinction and the browser already computes it.
   */
  const onFocusIn = (ev: FocusEvent): void => {
    const btn = targetFrom(ev.target)
    if (!btn) return
    try {
      if (!btn.matches(':focus-visible')) return
    } catch {
      return // no :focus-visible support; hover is enough
    }
    show(btn)
  }

  const onFocusOut = (ev: FocusEvent): void => {
    if (targetFrom(ev.target) === current) hide()
  }

  /**
   * A click either flips the label (play/pause, mute, fullscreen) or opens a
   * panel the tooltip would sit on top of. Re-reading after the frame settles
   * covers the first; the button losing its scope or label covers the second,
   * because show() hides when there is nothing to say.
   */
  const onClick = (ev: MouseEvent): void => {
    const btn = targetFrom(ev.target)
    if (!btn) return
    if (btn.hasAttribute('data-lp-tip-hide-on-click')) {
      hide()
      return
    }
    requestAnimationFrame(() => {
      if (current === btn && stage.contains(btn)) show(btn)
    })
  }

  stage.addEventListener('pointerover', onOver)
  stage.addEventListener('pointermove', track, { passive: true })
  stage.addEventListener('pointerout', onOut)
  stage.addEventListener('focusin', onFocusIn)
  stage.addEventListener('focusout', onFocusOut)
  stage.addEventListener('click', onClick, true)

  // The control bar fades out on inactivity and in fullscreen; a tooltip left
  // floating over the video would be the only thing still lit.
  const onLeave = (): void => hide()
  stage.addEventListener('pointerleave', onLeave)
  document.addEventListener('fullscreenchange', onLeave)
  window.addEventListener('resize', onLeave)
  window.addEventListener('scroll', onLeave, true)

  /**
   * Strip native titles as they appear. video.js sets `title` from
   * controlText() every time a button's state changes, so a sweep at mount
   * would be undone by the first play/pause.
   */
  const stripTitles = (root: ParentNode): void => {
    root.querySelectorAll<HTMLElement>('[title]').forEach((el) => {
      if (!el.closest(SCOPES)) return
      const t = el.getAttribute('title')
      if (t) el.setAttribute('data-lp-title', t)
      el.removeAttribute('title')
    })
  }

  const observer = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'attributes' && r.target instanceof HTMLElement) {
        const el = r.target
        if (!el.hasAttribute('title') || !el.closest(SCOPES)) continue
        const t = el.getAttribute('title')
        if (t) el.setAttribute('data-lp-title', t)
        el.removeAttribute('title')
        // The visible label may have changed with it.
        if (el === current) show(el)
      } else if (r.type === 'childList') {
        r.addedNodes.forEach((n) => {
          if (n instanceof Element) stripTitles(n.parentNode ?? n)
        })
      }
    }
  })

  stripTitles(stage)
  observer.observe(stage, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['title'],
  })

  return {
    dispose(): void {
      observer.disconnect()
      stage.removeEventListener('pointerover', onOver)
      stage.removeEventListener('pointermove', track)
      stage.removeEventListener('pointerout', onOut)
      stage.removeEventListener('focusin', onFocusIn)
      stage.removeEventListener('focusout', onFocusOut)
      stage.removeEventListener('click', onClick, true)
      stage.removeEventListener('pointerleave', onLeave)
      document.removeEventListener('fullscreenchange', onLeave)
      window.removeEventListener('resize', onLeave)
      window.removeEventListener('scroll', onLeave, true)
      tip.remove()
    },
  }
}
